import { Card, CardContent } from "@/components/ui/card";
import { useAnsweringSubjects } from "@/lib/hooks/use-flow-instances/use-answering-subjects";
import {
  useDeclareExperiences,
  useProductAssessmentAssessments,
  useProductAssessmentFlowInstanceValue,
  useProductTypesInUse,
  useStartQuizAttempt,
  useSubmitQuestionnaire,
  useSubmitQuizAttempt,
} from "@/lib/hooks/use-flow-instances/use-product-assessment";
import {
  useApplyPreviousAnswer,
  useReuseAvailability,
} from "@/lib/hooks/use-flow-instances/use-reuse-previous-answer";
import { useT } from "@/lib/i18n/use-t";
import { ProductAssessmentWidget } from "../product-assessment";
import type { ProductType } from "./flow-wizard-types";
import { T } from "./wizard-product-assessment.i18n";

type WizardProductAssessmentProps = {
  instanceId: string;
  portfolioCollectionId: string | null;
  portfolioCollectionName: string;
  productTypes: ProductType[];
  disabled: boolean;
  style?: "none" | "border" | "box";
  title?: string;
  description?: string;
  hasValidationError?: boolean;
};

export function WizardProductAssessment({
  instanceId,
  portfolioCollectionId,
  portfolioCollectionName,
  productTypes,
  disabled,
  style,
  title,
  description,
  hasValidationError,
}: WizardProductAssessmentProps) {
  const t = useT(T);

  const {
    inputData,
    calculatedData,
    isLoading: valueLoading,
  } = useProductAssessmentFlowInstanceValue(instanceId);
  const {
    available,
    sourceDate,
    isLoading: availabilityLoading,
  } = useReuseAvailability(instanceId, "product_assessment");
  const { assessments, isLoading: assessmentsLoading } =
    useProductAssessmentAssessments(instanceId);
  const productTypeIdsInUse = useProductTypesInUse(instanceId);
  const { attribution, isLoading: attributionLoading } = useAnsweringSubjects(instanceId);
  const declareExperiences = useDeclareExperiences(instanceId);
  const submitQuestionnaire = useSubmitQuestionnaire(instanceId);
  const startQuiz = useStartQuizAttempt(instanceId);
  const submitQuiz = useSubmitQuizAttempt(instanceId);
  const applyPrevious = useApplyPreviousAnswer(instanceId, "product_assessment");

  const isMembersMode = attribution?.kind === "members";

  if (valueLoading || availabilityLoading || assessmentsLoading || attributionLoading) return null;

  // Only show if there are product types and a portfolio collection
  if (productTypes.length === 0 || !portfolioCollectionId) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">{t.noProductTypesConfigured}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <ProductAssessmentWidget
        portfolioCollectionId={portfolioCollectionId}
        portfolioCollectionName={portfolioCollectionName}
        productTypes={productTypes}
        inputData={inputData}
        calculatedData={calculatedData}
        style={style}
        title={title}
        description={description}
        onDeclare={(declarations, subjectId) =>
          declareExperiences.mutateAsync({ declarations, subjectId })
        }
        onSubmitQuestionnaire={(request, subjectId) =>
          submitQuestionnaire.mutateAsync({ ...request, subjectId })
        }
        disabled={disabled}
        assessments={assessments}
        productTypeIdsInUse={productTypeIdsInUse ?? undefined}
        onStartQuiz={(productTypeId, subjectId) =>
          startQuiz.mutateAsync({ productTypeId, subjectId })
        }
        onSubmitQuiz={(request, subjectId) => submitQuiz.mutateAsync({ ...request, subjectId })}
        // QL-505 — the group-level offer would apply one answer set to every
        // member; per-member reuse is not offered on the advisor side yet.
        available={available && !isMembersMode}
        sourceDate={sourceDate}
        onApply={async () => {
          await applyPrevious.mutateAsync();
        }}
        hasValidationError={hasValidationError}
        attribution={attribution ?? undefined}
      />
    </div>
  );
}
